// src/features/settings/hooks/useNonWorkingDays.js
// Unified hook for public holidays + closing days ("non-working days").
// Replaces the old useClosingDays.js and usePublicHolidays.js hooks.
import { useState, useCallback } from 'react';
import { nonWorkingDaysAPI } from '../services/nonWorkingDays';
import { useApi } from '../../../hooks/api/useApi';

export function useNonWorkingDays() {
  const [nonWorkingDays, setNonWorkingDays] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentNonWorkingDay, setCurrentNonWorkingDay] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchNonWorkingDays = useCallback(async (params = {}) => {
    const result = await execute(() => nonWorkingDaysAPI.list(params));
    if (result.success) {
      setNonWorkingDays(result.data.non_working_days || []);
      setTotalCount(result.data.total || 0);
    }
    return result;
  }, [execute]);

  const fetchNonWorkingDayById = useCallback(async (nonWorkingDayId) => {
    const result = await execute(() => nonWorkingDaysAPI.getById(nonWorkingDayId));
    if (result.success) {
      setCurrentNonWorkingDay(result.data);
    }
    return result;
  }, [execute]);

  const fetchExpanded = useCallback(async (startDate, endDate, kind) => {
    return execute(() => nonWorkingDaysAPI.getExpanded(startDate, endDate, kind));
  }, [execute]);

  // --- Public holidays ---

  const createPublicHoliday = useCallback(async (data) => {
    const result = await execute(() => nonWorkingDaysAPI.createPublicHoliday(data));
    if (result.success) {
      await fetchNonWorkingDays();
    }
    return result;
  }, [execute, fetchNonWorkingDays]);

  const updatePublicHolidayHours = useCallback(async (nonWorkingDayId, data) => {
    const result = await execute(() => nonWorkingDaysAPI.updatePublicHolidayHours(nonWorkingDayId, data));
    if (result.success) {
      setNonWorkingDays((prev) => prev.map((d) =>
        d.non_working_day_id === nonWorkingDayId ? { ...d, ...result.data } : d
      ));
      if (currentNonWorkingDay?.non_working_day_id === nonWorkingDayId) {
        setCurrentNonWorkingDay((prev) => ({ ...prev, ...result.data }));
      }
    }
    return result;
  }, [execute, currentNonWorkingDay]);

  const generateHolidays = useCallback(async (data) => {
    const result = await execute(() => nonWorkingDaysAPI.generateHolidays(data));
    if (result.success) {
      await fetchNonWorkingDays();
    }
    return result;
  }, [execute, fetchNonWorkingDays]);

  const adminGenerateHolidaysForDepartment = useCallback(async (departmentId, data) => {
    return execute(() => nonWorkingDaysAPI.adminGenerateHolidaysForDepartment(departmentId, data));
  }, [execute]);

  // --- Closing days ---

  const createClosingDayDateRange = useCallback(async (data) => {
    const result = await execute(() => nonWorkingDaysAPI.createClosingDayDateRange(data));
    if (result.success) {
      await fetchNonWorkingDays();
    }
    return result;
  }, [execute, fetchNonWorkingDays]);

  const createClosingDayRRule = useCallback(async (data) => {
    const result = await execute(() => nonWorkingDaysAPI.createClosingDayRRule(data));
    if (result.success) {
      await fetchNonWorkingDays();
    }
    return result;
  }, [execute, fetchNonWorkingDays]);

  const updateClosingDayDateRange = useCallback(async (nonWorkingDayId, data) => {
    const result = await execute(() => nonWorkingDaysAPI.updateClosingDayDateRange(nonWorkingDayId, data));
    if (result.success) {
      setNonWorkingDays((prev) => prev.map((d) =>
        d.non_working_day_id === nonWorkingDayId ? { ...d, ...result.data } : d
      ));
      if (currentNonWorkingDay?.non_working_day_id === nonWorkingDayId) {
        setCurrentNonWorkingDay((prev) => ({ ...prev, ...result.data }));
      }
    }
    return result;
  }, [execute, currentNonWorkingDay]);

  const updateClosingDayRRule = useCallback(async (nonWorkingDayId, data) => {
    const result = await execute(() => nonWorkingDaysAPI.updateClosingDayRRule(nonWorkingDayId, data));
    if (result.success) {
      setNonWorkingDays((prev) => prev.map((d) =>
        d.non_working_day_id === nonWorkingDayId ? { ...d, ...result.data } : d
      ));
      if (currentNonWorkingDay?.non_working_day_id === nonWorkingDayId) {
        setCurrentNonWorkingDay((prev) => ({ ...prev, ...result.data }));
      }
    }
    return result;
  }, [execute, currentNonWorkingDay]);

  // --- Shared ---

  const deleteNonWorkingDay = useCallback(async (nonWorkingDayId) => {
    const result = await execute(() => nonWorkingDaysAPI.deleteNonWorkingDay(nonWorkingDayId));
    if (result.success) {
      setNonWorkingDays((prev) => prev.filter((d) => d.non_working_day_id !== nonWorkingDayId));
      setTotalCount((prev) => Math.max(0, prev - 1));
    }
    return result;
  }, [execute]);

  return {
    nonWorkingDays,
    currentNonWorkingDay,
    totalCount,
    loading,
    error,
    clearError,
    fetchNonWorkingDays,
    fetchNonWorkingDayById,
    fetchExpanded,
    createPublicHoliday,
    updatePublicHolidayHours,
    generateHolidays,
    adminGenerateHolidaysForDepartment,
    createClosingDayDateRange,
    createClosingDayRRule,
    updateClosingDayDateRange,
    updateClosingDayRRule,
    deleteNonWorkingDay,
  };
}
